import { ArrowRight, ArrowLeft, Shield, Search, Target, Cloud, Lock, Settings, CheckCircle, Eye, Zap, Activity, Database, Server, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from 'react';
import PageLayout from '@/components/PageLayout';
import { Card, CardContent } from "@/components/ui/card";
import SectionDivider from '@/components/SectionDivider';
import SEO from '@/components/SEO';

const CloudSecurity = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [activeService, setActiveService] = useState(0);

  const cloudServices = [
    {
      icon: <Search className="w-8 h-8" />,
      title: "Security Assessment",
      subtitle: "Comprehensive evaluation of cloud infrastructure and vulnerabilities",
      description: "Deep security analysis across cloud environments, from architecture design to vulnerability identification and penetration testing using real-world attack scenarios.",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
      borderColor: "rgba(59, 130, 246, 0.3)",
      iconColor: "#3b82f6",
      capabilities: [
        {
          title: "Cloud Security Architecture Review",
          description: "Infrastructure design evaluation against industry standards and best practices"
        },
        {
          title: "Vulnerability Assessment on Cloud Resources",
          description: "Cloud asset scanning for vulnerabilities across VMs, containers, and serverless"
        },
        {
          title: "Cloud Penetration Testing",
          description: "Real-world attack simulation across cloud applications and infrastructure"
        }
      ]
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: "Compliance & Governance",
      subtitle: "Regulatory alignment and security controls validation",
      description: "Comprehensive compliance readiness and security controls assessment to ensure your cloud operations meet regulatory requirements and industry standards.",
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      borderColor: "rgba(16, 185, 129, 0.3)",
      iconColor: "#10b981",
      capabilities: [
        {
          title: "Cloud Security Controls Audit",
          description: "Security controls assessment across IAM, logging, and encryption domains"
        },
        {
          title: "Cloud Compliance Readiness",
          description: "Compliance audit preparation and regulatory alignment assessment"
        }
      ]
    },
    {
      icon: <Activity className="w-8 h-8" />,
      title: "Operations & DevSecOps",
      subtitle: "Secure operations and CI/CD pipeline integration",
      description: "Operational security assessments and DevSecOps integration to embed security throughout your cloud operations and software delivery lifecycle.",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      borderColor: "rgba(245, 158, 11, 0.3)",
      iconColor: "#f59e0b",
      capabilities: [
        {
          title: "Cloud Governance and Operations Assessments",
          description: "Policy and identity management review for multi-cloud environments"
        },
        {
          title: "DevSecOps Integration",
          description: "Security automation embedded into CI/CD pipelines and delivery lifecycle"
        }
      ]
    }
  ];

  const benefits = [
    {
      icon: <Cloud className="w-12 h-12" />,
      title: "Multi-Cloud Expertise",
      description: "Deep knowledge across AWS, Azure, GCP, and hybrid environments",
      color: "#3b82f6"
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Proactive Security",
      description: "Identify and remediate vulnerabilities before they become threats",
      color: "#10b981"
    },
    {
      icon: <Activity className="w-12 h-12" />,
      title: "Continuous Monitoring",
      description: "Ongoing security monitoring and compliance validation",
      color: "#f59e0b"
    }
  ];

  const complianceStandards = [
    "SOC 2 Type II",
    "ISO 27001",
    "HIPAA",
    "FedRAMP"
  ];

  return (
    <PageLayout>
      <SEO 
        title="Cloud Security Services | AWS Azure GCP Security Assessment & Penetration Testing"
        description="Expert cloud security services including multi-cloud architecture review, cloud penetration testing, DevSecOps integration, and compliance auditing for AWS, Azure, GCP environments by certified security professionals."
        keywords={[
          'cloud security services',
          'AWS security assessment',
          'Azure security testing',
          'GCP security audit',
          'cloud penetration testing',
          'multi-cloud security',
          'cloud security architecture review',
          'cloud compliance readiness',
          'DevSecOps cloud integration',
          'cloud security consulting',
          'cloud vulnerability assessment',
          'hybrid cloud security',
          'cloud security monitoring',
          'cloud governance assessment',
          'container security cloud'
        ]}
      />
      
      {/* Hero Section */}
      <section
        className="pt-24 pb-16 relative overflow-hidden"
        style={{
          background: "#0f0f17",
          color: "#fff",
        }}
      >
        {/* Animated background */}
        <div className="absolute inset-0 overflow-hidden z-0">
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 10% 20%, rgba(59, 130, 246, 0.15) 0%, transparent 50%)" }} />
          <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 90% 80%, rgba(16, 185, 129, 0.15) 0%, transparent 50%)" }} />
        </div>

        <div className="container mx-auto px-8 relative z-10" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <Link
              to="/services"
              className="inline-flex items-center mb-8 transition-colors group rounded-full px-5 py-2 font-semibold shadow-lg"
              style={{
                background: "linear-gradient(135deg, #3b82f6 0%, #10b981 100%)",
                color: "#fff",
                fontWeight: 600,
                fontSize: 16,
                borderRadius: "999px",
                boxShadow: "0 4px 24px 0 rgba(59, 130, 246, 0.18)"
              }}
            >
              <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              Back to Services
            </Link>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <div
                className="inline-block mb-4 px-6 py-3 text-sm font-medium border shadow-lg"
                style={{
                  background: "linear-gradient(135deg, #3b82f6 0%, #10b981 100%)",
                  color: "#ffffff",
                  borderRadius: "999px",
                  borderColor: "rgba(59, 130, 246, 0.3)",
                  borderWidth: 1.5,
                  boxShadow: "0 4px 24px 0 rgba(59, 130, 246, 0.25)",
                  fontWeight: 600,
                  letterSpacing: "0.02em",
                }}
              >
                <Cloud className="w-4 h-4 inline mr-2" />
                Cloud Security
              </div>
              <h1
                className="mb-6"
                style={{
                  fontSize: "clamp(2.5rem, 6vw, 3.75rem)",
                  fontWeight: 700,
                  lineHeight: 1.1,
                  color: "#fff",
                  letterSpacing: "-0.025em",
                }}
              >
                <span
                  className="block"
                  style={{
                    background: "linear-gradient(135deg, #3b82f6 0%, #10b981 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    color: "#3b82f6",
                    fontWeight: 700,
                  }}
                >
                  Secure Your Cloud Journey
                </span>
                <span className="block mt-2" style={{ fontSize: "0.7em", color: "#e5e7eb" }}>
                  From Architecture to Compliance
                </span>
              </h1>
              <p
                className="max-w-3xl mx-auto"
                style={{
                  fontSize: "1.125rem",
                  color: "#d1d5db",
                  lineHeight: 1.7,
                  fontWeight: 400,
                }}
              >
                Comprehensive cloud security services to protect your infrastructure, applications, and data across AWS, Azure, GCP, and hybrid environments.
              </p>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Service Categories */}
      <section
        className="py-16 px-8"
        style={{
          background: "#0f0f17",
        }}
      >
        <div className="container mx-auto" style={{ maxWidth: 1280 }}>
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2
                style={{
                  fontSize: "2.25rem",
                  fontWeight: 700,
                  color: "#fff",
                  letterSpacing: "-0.01em",
                  marginBottom: 16,
                }}
              >
                Comprehensive Cloud Security Services
              </h2>
              <p
                style={{
                  fontSize: "1.125rem",
                  color: "#d1d5db",
                  maxWidth: 800,
                  margin: "0 auto",
                }}
              >
                From architecture to compliance - complete cloud security solutions
              </p>
            </motion.div>

            {/* Service Navigation */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
              {cloudServices.map((service, index) => (
                <button
                  key={index}
                  onClick={() => setActiveService(index)}
                  className={`px-3 py-4 rounded-lg font-medium transition-all duration-300 text-center ${
                    activeService === index ? 'shadow-lg' : 'opacity-70 hover:opacity-100'
                  }`}
                  style={{
                    background: activeService === index ? service.gradient : 'rgba(15, 15, 23, 0.85)',
                    border: `1.5px solid ${activeService === index ? service.borderColor : 'rgba(255, 255, 255, 0.1)'}`,
                    color: "#fff",
                    minHeight: "100px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    padding: "16px 12px"
                  }}
                >
                  <div style={{ flexShrink: 0 }}>
                    {service.icon}
                  </div>
                  <span className="text-xs sm:text-sm leading-tight" style={{ wordBreak: "break-word" }}>
                    {service.title}
                  </span>
                </button>
              ))}
            </div>

            {/* Active Service Details */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeService}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4 }}
              >
                <Card
                  className="mb-8"
                  style={{
                    background: "rgba(15, 15, 23, 0.85)",
                    border: `2px solid ${cloudServices[activeService].borderColor}`,
                    borderRadius: 16,
                  }}
                >
                  <CardContent className="p-4 sm:p-6 md:p-8">
                    <div className="flex flex-col sm:flex-row items-start mb-8">
                      <div
                        className="flex items-center justify-center mb-4 sm:mb-0 sm:mr-6 flex-shrink-0"
                        style={{
                          width: 64,
                          height: 64,
                          background: cloudServices[activeService].gradient,
                          borderRadius: 16,
                        }}
                      >
                        {cloudServices[activeService].icon}
                      </div>
                      <div className="flex-1">
                        <h3
                          className="text-xl sm:text-2xl md:text-3xl"
                          style={{
                            fontWeight: 700,
                            color: "#fff",
                            marginBottom: 8,
                          }}
                        >
                          {cloudServices[activeService].title}
                        </h3>
                        <p
                          className="text-base sm:text-lg"
                          style={{
                            color: cloudServices[activeService].iconColor,
                            fontWeight: 600,
                            marginBottom: 16,
                          }}
                        >
                          {cloudServices[activeService].subtitle}
                        </p>
                        <p
                          className="text-sm sm:text-base"
                          style={{
                            color: "#d1d5db",
                            lineHeight: 1.7,
                          }}
                        >
                          {cloudServices[activeService].description}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      {cloudServices[activeService].capabilities.map((capability, idx) => (
                        <div
                          key={idx}
                          className="p-4 sm:p-6 rounded-lg"
                          style={{
                            background: "rgba(0, 0, 0, 0.3)",
                            border: "1px solid rgba(255, 255, 255, 0.1)",
                          }}
                        >
                          <div className="flex items-start">
                            <Cloud
                              className="w-5 h-5 mr-3 mt-1 flex-shrink-0"
                              style={{ color: cloudServices[activeService].iconColor }}
                            />
                            <div className="flex-1">
                              <h4
                                className="text-base sm:text-lg"
                                style={{
                                  fontWeight: 600,
                                  color: "#fff",
                                  marginBottom: 8,
                                }}
                              >
                                {capability.title}
                              </h4>
                              <p
                                className="text-xs sm:text-sm"
                                style={{
                                  color: "#9ca3af",
                                  lineHeight: 1.6,
                                }}
                              >
                                {capability.description}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </AnimatePresence>

            {/* Benefits Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              {benefits.map((benefit, index) => (
                <div key={index} className="text-center">
                  <div 
                    className="flex items-center justify-center mx-auto mb-4" 
                    style={{ 
                      color: benefit.color,
                      width: "48px",
                      height: "48px"
                    }}
                  >
                    {benefit.icon}
                  </div>
                  <h4 style={{ fontWeight: 700, color: "#fff", marginBottom: 8, fontSize: 20 }}>
                    {benefit.title}
                  </h4>
                  <p style={{ color: "#9ca3af", fontSize: 15, lineHeight: 1.6 }}>
                    {benefit.description}
                  </p>
                </div>
              ))}
            </motion.div>

            {/* Compliance Standards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="mt-16"
            >
              <Card
                style={{
                  background: "linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(16, 185, 129, 0.1) 100%)",
                  border: "2px solid rgba(59, 130, 246, 0.3)",
                  borderRadius: 16,
                }}
              >
                <CardContent className="p-8">
                  <div className="text-center mb-8">
                    <h3
                      style={{
                        fontSize: "1.75rem",
                        fontWeight: 700,
                        color: "#fff",
                        marginBottom: 8,
                      }}
                    >
                      Compliance Readiness Standards
                    </h3>
                    <p
                      style={{
                        color: "#d1d5db",
                        fontSize: 16,
                        maxWidth: 600,
                        margin: "0 auto",
                      }}
                    >
                      Prepare for and maintain compliance with major regulatory frameworks
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {complianceStandards.map((standard, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-center"
                        style={{
                          color: "#fff",
                          fontSize: 16,
                          fontWeight: 600,
                          padding: "1rem",
                          background: "rgba(59, 130, 246, 0.1)",
                          borderRadius: 8,
                          border: "1px solid rgba(59, 130, 246, 0.2)",
                        }}
                      >
                        <CheckCircle className="w-5 h-5 mr-2" style={{ color: "#10b981" }} />
                        {standard}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="mt-16 text-center"
              style={{
                background: "rgba(15, 15, 23, 0.85)",
                borderRadius: 16,
                padding: "3rem",
                border: "1px solid rgba(59, 130, 246, 0.2)",
              }}
            >
              <h3
                style={{
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: "#fff",
                  marginBottom: 16,
                }}
              >
                Secure Your Cloud Infrastructure
              </h3>
              <p
                style={{
                  color: "#d1d5db",
                  fontSize: 18,
                  marginBottom: 32,
                  maxWidth: 600,
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                Partner with our cloud security experts to build, assess, and maintain secure cloud environments that scale with your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contactus"
                  className="inline-flex items-center font-medium transition-all shadow-lg hover:shadow-xl"
                  style={{
                    background: "linear-gradient(135deg, #3b82f6 0%, #10b981 100%)",
                    color: "#ffffff",
                    padding: "1rem 2rem",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    boxShadow: "0 4px 24px 0 rgba(59, 130, 246, 0.25)",
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = "linear-gradient(135deg, #2563eb 0%, #059669 100%)";
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = "linear-gradient(135deg, #3b82f6 0%, #10b981 100%)";
                  }}
                >
                  Schedule Cloud Security Assessment
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center font-medium transition-all"
                  style={{
                    background: "transparent",
                    color: "#3b82f6",
                    padding: "1rem 2rem",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "1.125rem",
                    border: "1.5px solid #3b82f6",
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = "rgba(59, 130, 246, 0.1)";
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  View All Services
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default CloudSecurity;